import { Notification } from '@thanx/uikit/notification'
import { Text } from '@thanx/uikit/text'
import {
  merchantUsersApi,
  Role,
  UpdateMerchantUserPayload,
} from 'api/merchantUsers'
import { rolesApi } from 'api/roles'
import { Role as RbacRole } from 'api/roles'
import Spinner from 'components/Spinner'
import Formsy from 'formsy-react'
import { useDisplayMessage } from 'hooks/useDisplayMessage'
import useFlag from 'hooks/useFlag'
import { useIsRbacAdmin } from 'hooks/useIsRbacAdmin'
import { buildTranslate } from 'locales'
import { useEffect, useRef, useState } from 'react'
import { useDispatch } from 'react-redux'
import { RouteComponentProps } from 'react-router-dom'
import { push } from 'react-router-redux'
import { useStyletron } from 'styletron-react'
import FormLayout from './FormLayout'
import MerchantUserForm from './MerchantUserForm'
import { RbacLocationsSection } from './RbacLocationsSection'
import { RbacRoleDropdown } from './RbacRoleDropdown'

type Props = {} & RouteComponentProps<{ id: string }>
const t = buildTranslate('merchant_users.edit')

const EditMerchantUser = ({ match }: Props) => {
  const dispatch = useDispatch()
  const rbacEnabled = useFlag('role-based-access-control', false)
  const isAdmin = useIsRbacAdmin()

  const merchantUserId = match.params.id

  const {
    data: { merchant_user: merchantUser } = {},
    isLoading,
    isSuccess,
    refetch: refetchUser,
  } = merchantUsersApi.useGetMerchantUserQuery(merchantUserId)

  const [updateMerchantUser, updateUserStatus] =
    merchantUsersApi.useUpdateMerchantUserMutation({
      fixedCacheKey: 'update',
    })

  const [assignRole] = merchantUsersApi.useAssignRoleMutation()
  const [removeRole] = merchantUsersApi.useRemoveRoleMutation()

  const [sendInvitation, inviteUserStatus] =
    merchantUsersApi.useSendInvitationMutation({
      fixedCacheKey: 'invitation',
    })

  const [merchantUserPatch, setMerchantUserPatch] =
    useState<Partial<UpdateMerchantUserPayload>>()

  const [selectedRbacRoleIds, setSelectedRbacRoleIds] = useState<number[]>([])
  const [rbacLocationIds, setRbacLocationIds] = useState<number[]>([])
  const initialRoleIdsRef = useRef<number[]>([])
  const initialLocationIdsRef = useRef<number[]>([])

  const { data: rolesData } = rolesApi.useGetRolesQuery(undefined, {
    skip: !rbacEnabled,
  })
  const allRoles = rolesData?.roles ?? []

  const selectedRbacRoles: RbacRole[] = selectedRbacRoleIds
    .map(id => allRoles.find(r => r.id === id))
    .filter((r): r is RbacRole => Boolean(r))

  const hasCustomRole = selectedRbacRoles.some(r => !r.preset)

  useEffect(() => {
    if (allRoles.length === 0 || selectedRbacRoleIds.length === 0) return
    if (!hasCustomRole) {
      setRbacLocationIds([])
    }
  }, [hasCustomRole, allRoles.length, selectedRbacRoleIds.length])

  const displayUpdateStatusMessage = useDisplayMessage(updateUserStatus.reset)
  const displayInviteStatusMessage = useDisplayMessage(inviteUserStatus.reset)

  const [css] = useStyletron()

  useEffect(() => {
    if (isSuccess && merchantUser) {
      setMerchantUserPatch(prev => ({
        ...prev,
        role: merchantUser.role,
        location_ids: merchantUser.location_ids,
      }))
      const rbacRoleIds = merchantUser.rbac_role_ids ?? []
      setSelectedRbacRoleIds(rbacRoleIds)
      initialRoleIdsRef.current = rbacRoleIds
      const locationIds = merchantUser.location_ids ?? []
      setRbacLocationIds(locationIds)
      initialLocationIdsRef.current = locationIds
    }
  }, [merchantUser, isSuccess])

  const handleFinish = async () => {
    try {
      await updateMerchantUser({
        ...merchantUserPatch,
        id: +merchantUserId,
      }).unwrap()
      dispatch(push('/merchant_users?tab=users'))
    } catch (err) {
      displayUpdateStatusMessage(t('error_generic'), 'danger')
    }
  }

  const handleRbacFinish = async () => {
    if (selectedRbacRoleIds.length === 0) return
    try {
      const locationsChanged =
        rbacLocationIds.length !== initialLocationIdsRef.current.length ||
        rbacLocationIds.some(
          id => !new Set(initialLocationIdsRef.current).has(id)
        )

      if (locationsChanged) {
        await updateMerchantUser({
          id: +merchantUserId,
          role: merchantUser?.role,
          location_ids: rbacLocationIds,
        }).unwrap()
      }

      const initialSet = new Set(initialRoleIdsRef.current)
      const finalSet = new Set(selectedRbacRoleIds)
      const toRemove = initialRoleIdsRef.current.filter(id => !finalSet.has(id))
      const toAdd = selectedRbacRoleIds.filter(id => !initialSet.has(id))

      const removePromises = toRemove.map(roleId =>
        removeRole({ merchantUserId: +merchantUserId, roleId }).unwrap()
      )
      const addPromises = toAdd.map(roleId =>
        assignRole({ merchantUserId: +merchantUserId, roleId }).unwrap()
      )
      await Promise.all([...removePromises, ...addPromises])
      dispatch(push('/merchant_users?tab=users'))
    } catch (err) {
      const { data } = await refetchUser()
      if (data?.merchant_user?.rbac_role_ids) {
        setSelectedRbacRoleIds(data.merchant_user.rbac_role_ids)
        initialRoleIdsRef.current = data.merchant_user.rbac_role_ids
      }
      displayUpdateStatusMessage(t('error_generic'), 'danger')
    }
  }

  const handleSendInvitation = async () => {
    try {
      await sendInvitation(merchantUserId).unwrap()
      dispatch(push('/merchant_users?tab=users'))
    } catch (err) {
      displayInviteStatusMessage(t('invitation_error'), 'danger')
    }
  }

  if (rbacEnabled) {
    return (
      <Spinner isLoading={isLoading}>
        <FormLayout
          isEditing
          canSendInvitation={merchantUser?.pending}
          onFinish={handleRbacFinish}
          onSendInvitation={handleSendInvitation}
          canProceed={selectedRbacRoleIds.length > 0}
          hideFinish={!isAdmin}
        >
          <Formsy role="form">
            {!isAdmin && (
              <div className="mb-l">
                <Notification kind="warning" size="full">
                  <div className="d-flex p-xs align-items-center">
                    <i
                      className="fa fa-exclamation-triangle mr-xs"
                      aria-hidden="true"
                    />
                    <Text.BodyText4>
                      {t('admin_required_warning')}
                    </Text.BodyText4>
                  </div>
                </Notification>
              </div>
            )}
            <div className="mb-l">
              {merchantUser?.first_name && merchantUser?.last_name && (
                <Text.Header2>{`${merchantUser.first_name} ${merchantUser.last_name}`}</Text.Header2>
              )}
              <Text.BodyText3 color="grey70">
                {merchantUser?.email}
              </Text.BodyText3>
            </div>
            <RbacRoleDropdown
              selectedRoleIds={selectedRbacRoleIds}
              onChange={setSelectedRbacRoleIds}
              locationIds={rbacLocationIds}
              disabled={!isAdmin}
            />
            <RbacLocationsSection
              roles={selectedRbacRoles}
              locationIds={rbacLocationIds}
              onChange={setRbacLocationIds}
              disabled={!isAdmin}
            />
          </Formsy>
        </FormLayout>
      </Spinner>
    )
  }

  return (
    <Spinner isLoading={isLoading}>
      <FormLayout
        isEditing
        canSendInvitation={merchantUser?.pending}
        onFinish={handleFinish}
        onSendInvitation={handleSendInvitation}
      >
        <Formsy role="form">
          <div className={css({ marginBottom: '40px' })}>
            {merchantUser?.first_name && merchantUser?.last_name && (
              <Text.Header2>{`${merchantUser.first_name} ${merchantUser.last_name}`}</Text.Header2>
            )}
            <Text.BodyText3 color="#6A747F">
              {merchantUser?.email}
            </Text.BodyText3>
          </div>

          {merchantUserPatch && (
            <MerchantUserForm
              merchantUser={merchantUserPatch}
              onChange={nextMerchantUser => {
                setMerchantUserPatch(prev => ({
                  ...prev,
                  location_ids: nextMerchantUser.location_ids,
                  role: nextMerchantUser.role as Role,
                }))
              }}
            />
          )}
        </Formsy>
      </FormLayout>
    </Spinner>
  )
}

export default EditMerchantUser
